import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import MemberDraftProductCard from './MemberDraftProductCard';
import DraftEvolutionView from './DraftEvolutionView';
import DraftSupplierCheckIn from './DraftSupplierCheckIn';
import { useOrientation } from '../hooks/useOrientation';
import CompareView from './CompareView';
import SavedShortlist from './SavedShortlist';

const PAGE_SIZE = 25;
const BROWSE_CATEGORIES = [
  { key: 'pantry', label: 'Pantry' },
  { key: 'dairy-eggs-fridge', label: 'Dairy & Eggs' },
  { key: 'fruit-vegetables', label: 'Fruit & Veg' },
  { key: 'meat-seafood', label: 'Meat & Seafood' },
  { key: 'bakery', label: 'Bakery' },
  { key: 'deli', label: 'Deli' },
  { key: 'frozen', label: 'Frozen' },
  { key: 'drinks', label: 'Drinks' },
  { key: 'household', label: 'Household' },
  { key: 'health-beauty', label: 'Health & Beauty' },
  { key: 'baby', label: 'Baby' },
  { key: 'pet', label: 'Pet' },
  { key: 'liquor', label: 'Liquor' },
];

export default function MemberDraft() {
  const { kinId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const orientation = useOrientation();
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('kinViewMode') || null;
  });
  const effectiveView = viewMode || orientation;

  useEffect(() => {
    if (viewMode) localStorage.setItem('kinViewMode', viewMode);
  }, [viewMode]);
  const [supplierSearch, setSupplierSearch] = useState({});

  // Draft state
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [tabView, setTabView] = useState('draft');

  // Suppliers and their products
  const [suppliers, setSuppliers] = useState([]);
  const [suppliersLoading, setSuppliersLoading] = useState(true);
  const [expandedSuppliers, setExpandedSuppliers] = useState({});
  const [supplierProducts, setSupplierProducts] = useState({});
  const [supplierPages, setSupplierPages] = useState({});
  const [supplierCategories, setSupplierCategories] = useState({});
  const [supplierCategoryFilter, setSupplierCategoryFilter] = useState({});
  const [loadingMore, setLoadingMore] = useState({});

  // --- Load draft state ---
  useEffect(() => {
    if (kinId) {
      fetchDraft();
      fetchSuppliers();
    }
  }, [kinId]);

  const fetchDraft = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/member-drafts/my/${kinId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setDraft(data.draft || data);
      } else {
        const err = await res.json();
        setError(err.error || 'Failed to fetch draft');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchSuppliers = async () => {
    setSuppliersLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/member-drafts/my/${kinId}/suppliers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setSuppliers(data.suppliers || []);
      }
    } catch (err) {
      console.error('Error fetching suppliers:', err);
    } finally {
      setSuppliersLoading(false);
    }
  };

  const fetchSupplierProducts = async (supplierId, page = 1, category = null, search = null) => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        supplier: supplierId,
        page: String(page),
        limit: String(PAGE_SIZE),
      });
      if (category) params.append('category', category);
      if (search) params.set('q', search);

      const res = await fetch(
        `/api/member-drafts/my/${kinId}/supplier-products?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) return;

      const data = await res.json();

      setSupplierProducts(prev => {
        const existing = prev[supplierId] || [];
        const combined = page === 1 ? data.products : [...existing, ...data.products];
        return { ...prev, [supplierId]: combined };
      });

      setSupplierPages(prev => ({ ...prev, [supplierId]: data.pagination }));
      setSupplierCategories(prev => ({ ...prev, [supplierId]: data.categories }));
    } catch (err) {
      console.error('Error fetching supplier products:', err);
    }
  };

  const toggleSupplier = (supplierId) => {
    const isExpanded = expandedSuppliers[supplierId];
    if (isExpanded) {
      setExpandedSuppliers(prev => ({ ...prev, [supplierId]: false }));
    } else {
      setExpandedSuppliers(prev => ({ ...prev, [supplierId]: true }));
      // Fetch on first expand only
      if (!supplierProducts[supplierId]) {
        fetchSupplierProducts(supplierId, 1, supplierCategoryFilter[supplierId] || null);
      }
    }
  };

  const setCategoryFilter = (supplierId, category) => {
    setSupplierCategoryFilter(prev => ({ ...prev, [supplierId]: category || null }));
    // Reset products and reload
    setSupplierProducts(prev => ({ ...prev, [supplierId]: [] }));
    fetchSupplierProducts(supplierId, 1, category || null);
  };

  const loadMore = async (supplierId) => {
    const pagination = supplierPages[supplierId];
    if (!pagination || !pagination.page || pagination.page >= pagination.pages) return;
    setLoadingMore(prev => ({ ...prev, [supplierId]: true }));
    await fetchSupplierProducts(
      supplierId,
      pagination.page + 1,
      supplierCategoryFilter[supplierId] || null
    );
    setLoadingMore(prev => ({ ...prev, [supplierId]: false }));
  };

  // --- Draft item actions ---
  const handleAddItem = async (productId, quantity = 1) => {
    if (!productId) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/member-drafts/my/${kinId}/items`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ productId, quantity }),
      });
      if (res.ok) {
        const data = await res.json();
        setDraft(data);
      } else {
        const err = await res.json();
        alert(err.error || 'Failed to add item');
      }
    } catch (err) {
      alert('Error adding item');
    }
  };

  const handleRemoveItem = async (itemId) => {
    if (!window.confirm('Remove this item from your draft?')) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/member-drafts/my/${kinId}/items/${itemId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setDraft(data);
      } else {
        const err = await res.json();
        alert(err.error || 'Failed to remove item');
      }
    } catch (err) {
      alert('Error removing item');
    }
  };

  const handleUpdateQuantity = async (productId, quantity) => {
    try {
      const token = localStorage.getItem('token');
      const existingItem = draft?.items?.find(
        item => item.productId?.toString() === productId?.toString()
      );
      if (!existingItem) {
        if (quantity > 0) await handleAddItem(productId, quantity);
        return;
      }
      if (quantity === 0) {
        await handleRemoveItem(existingItem._id);
        return;
      }
      const res = await fetch(
        `/api/member-drafts/my/${kinId}/items/${existingItem._id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ quantity }),
        }
      );
      if (res.ok) {
        const data = await res.json();
        setDraft(data);
      }
    } catch (err) {
      console.error('Error updating quantity:', err);
    }
  };

// A quick remove without the confirm dialog, for the landscape
// comparison where taps should be immediate.
const handleQuickRemove = async (productId) => {
  const item = (draft?.items || []).find(
    (i) => i.productId?.toString() === productId?.toString()
  );
  if (!item) return;
  try {
    const token = localStorage.getItem('token');
    const res = await fetch(`/api/member-drafts/my/${kinId}/items/${item._id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) {
      const data = await res.json();
      setDraft(data);
    }
  } catch (err) {
    console.error('Error removing item:', err);
  }
};

  const handleAddFromCompare = async (productId, product, chain, quantityDelta = 1) => {
    if (quantityDelta > 0) {
      await handleAddItem(productId, quantityDelta);
      return;
    }
    const item = (draft?.items || []).find(
      (i) => i.productId?.toString() === productId?.toString()
    );
    if (!item) return;
    if (item.quantity > 1) {
      await handleUpdateQuantity(productId, item.quantity - 1);
    } else {
      await handleQuickRemove(productId);
    }
  };

const handleRemoveFromCompare = (productId) => handleQuickRemove(productId);

const draftMap = React.useMemo(() => {
  const map = {};
  for (const item of (draft?.items || [])) {
    if (item.productId) map[item.productId.toString()] = item.quantity;
  }
  return map;
}, [draft]);



  const submitDraft = async () => {
    if (!draft || draft.items.length === 0) {
      alert('Your draft is empty');
      return;
    }
    if (!window.confirm('Submit your draft to the coordinator? Once submitted, you cannot make changes.')) return;
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/member-drafts/my/${kinId}/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ notes: '' }),
      });
      if (res.ok) {
        const data = await res.json();
        setDraft(data.draft);
        alert('✅ Draft submitted successfully!');
      } else {
        const err = await res.json();
        alert(err.error || 'Failed to submit draft');
      }
    } catch (err) {
      alert('Error submitting draft');
    } finally {
      setSubmitting(false);
    }
  };

  const submitRevisions = async () => {
    if (!draft || draft.items.length === 0) {
      alert('Your draft is empty');
      return;
    }
    if (!window.confirm('Submit your revised draft to the coordinator? This will be your final order.')) return;
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/member-drafts/my/${kinId}/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ notes: 'Revised after consolidation' }),
      });
      if (res.ok) {
        const data = await res.json();
        setDraft(data.draft);
        alert('✅ Revisions submitted successfully!');
        navigate('/kin-dashboard');
      } else {
        const err = await res.json();
        alert(err.error || 'Failed to submit revisions');
      }
    } catch (err) {
      alert('Error submitting revisions');
    } finally {
      setSubmitting(false);
    }
  };

  // --- Helpers ---
  const isProductInDraft = (productId) =>
    draft?.items?.some(
      item => item.productId?.toString() === productId?.toString()
    );

  const getItemQuantity = (productId) => {
    const item = draft?.items?.find(
      item => item.productId?.toString() === productId?.toString()
    );
    return item?.quantity || 0;
  };

  const isReadOnly =
    draft?.status === 'finalized' || draft?.status === 'submitted';

  const getStatusInfo = () => {
    const statusMap = {
      draft: { icon: '📝', label: 'Draft', color: 'bg-gray-100 text-gray-700 border-gray-200', nextStep: 'Submit your draft to the coordinator' },
      submitted: { icon: '⏳', label: 'Submitted', color: 'bg-yellow-100 text-yellow-800 border-yellow-200', nextStep: 'Waiting for the coordinator to consolidate' },
      consolidated: { icon: '📊', label: 'Consolidated', color: 'bg-green-100 text-green-800 border-green-200', nextStep: 'Review and submit your revisions' },
      revising: { icon: '✏️', label: 'Revising', color: 'bg-blue-100 text-blue-800 border-blue-200', nextStep: 'Submit your revisions to the coordinator' },
      finalized: { icon: '🔒', label: 'Finalized', color: 'bg-purple-100 text-purple-800 border-purple-200', nextStep: 'Order sent to suppliers' },
    };
    return statusMap[draft?.status] || statusMap.draft;
  };

  // --- Render ---
  if (effectiveView === 'landscape') {
    const enabledSegments = new Set();
    for (const s of suppliers) {
      if (s.type === 'vhub' && s.category) enabledSegments.add(s.category);
    }
    return (
      <div className="h-screen flex flex-col">
        <CompareView
          kinId={kinId}
          categories={BROWSE_CATEGORIES}
          enabledSegments={[...enabledSegments]}
          onSwitchToPortrait={() => setViewMode('portrait')}
          onDraftChanged={fetchDraft}
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto p-4">
        <div className="text-center py-12">
          <div className="text-gray-600">Loading your draft...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-6xl mx-auto p-4">
        <div className="text-center py-12">
          <div className="text-red-600">Error: {error}</div>
          <button
            onClick={fetchDraft}
            className="mt-4 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!draft) {
    return (
      <div className="max-w-6xl mx-auto p-4">
        <div className="text-center py-12 text-gray-500">No draft found</div>
      </div>
    );
  }

  if (draft.status === 'finalized') {
    return (
      <div className="max-w-4xl mx-auto p-4">
        <div className="bg-green-50 border-2 border-green-300 rounded-lg p-8 text-center">
          <div className="text-6xl mb-4">✅</div>
          <h2 className="text-2xl font-bold text-green-700 mb-2">Order Finalized!</h2>
          <p className="text-green-600 mb-4">
            Your order has been finalized and sent to suppliers.
          </p>
          <button
            onClick={() => navigate('/kin-dashboard')}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 font-medium"
          >
            📊 View Kin Dashboard
          </button>
        </div>
      </div>
    );
  }

  const statusInfo = getStatusInfo();
  const hasActiveDraft = draft.items && draft.items.length > 0;
  const canSubmit = draft.status === 'draft';
  const isConsolidated = draft.status === 'consolidated' || draft.status === 'revising';

  return (
    <div className="max-w-6xl mx-auto p-4">
      {/* Status banner */}
      <div className={`border rounded-lg p-3 mb-4 flex flex-wrap items-center justify-between gap-2 ${statusInfo.color}`}>
        <div className="flex items-center gap-2">
          <span className="text-lg">{statusInfo.icon}</span>
          <span className="font-medium">Status: {statusInfo.label}</span>
          <span className="text-sm opacity-75">• {statusInfo.nextStep}</span>
        </div>
        <div className="text-sm">
          {draft.items.length} items • ${draft.total?.toFixed(2) || '0.00'}
        </div>
      </div>

      {/* Mode toggle */}
      <div className="mb-3">
        <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
          <button
            type="button"
            onClick={() => setViewMode('landscape')}
            title="Product Research: compare products and prices across stores, and shortlist what interests you."
            className={`px-3 py-1.5 text-sm font-medium ${
              effectiveView === 'landscape' ? 'bg-green-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            ◨ Product Research
          </button>
          <button
            type="button"
            onClick={() => setViewMode('portrait')}
            title="Draft Order: review your shortlist, set quantities, and place your order."
            className={`px-3 py-1.5 text-sm font-medium ${
              effectiveView === 'portrait' ? 'bg-green-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            ◧ Draft Order
          </button>
        </div>
        <div className="text-xs text-gray-500 mt-2 space-y-1">
          <div><span className="font-medium text-gray-700">◨ Product Research:</span> compare products and prices across stores, and shortlist what interests you.</div>
          <div><span className="font-medium text-gray-700">◧ Draft Order:</span> review your shortlist, set quantities, and place your order.</div>
        </div>
      </div>

      {isConsolidated && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 text-sm text-blue-700">
          💡 <strong>The Kin has been consolidated!</strong> Adjust quantities to unlock more savings.
        </div>
      )}

      {/* Tab switcher */}
      <div className="flex border-b border-gray-200 mb-4">
        <button
          onClick={() => setTabView('draft')}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            tabView === 'draft'
              ? 'border-b-2 border-green-600 text-green-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          📝 Current Draft
        </button>
        <button
          onClick={() => setTabView('shortlist')}
          className={`... ${tabView === 'shortlist' ? 'active' : ''}`}
        >
          Saved shortlist
        </button>
        <button
          onClick={() => setTabView('evolution')}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            tabView === 'evolution'
              ? 'border-b-2 border-green-600 text-green-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          📊 Evolution View
        </button>
      </div>
      {tabView === 'shortlist' ? (
        <SavedShortlist kinId={kinId} onAddToDraft={fetchDraft} />
      ) : tabView === 'evolution' ? (
        <DraftEvolutionView kinId={kinId} currentStatus={draft?.status} />
      ) : (
        <>
          <DraftSupplierCheckIn kinId={kinId} />
          {/* Current draft summary */}
          {hasActiveDraft && (
            <div className="bg-white rounded-lg shadow p-3 mb-4 border border-gray-100">
              <div className="flex justify-between items-center mb-2">
                <span className="font-medium text-sm">
                  🛒 {draft.items.length} items in draft
                </span>
                <span className="font-bold text-green-600">
                  ${draft.total?.toFixed(2) || '0.00'}
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-xs text-gray-500">
                      <th className="text-left py-1 px-2">Supplier</th>
                      <th className="text-left py-1 px-2">Segment</th>
                      <th className="text-left py-1 px-2">Product</th>
                      <th className="text-right py-1 px-2">Price</th>
                      <th className="text-center py-1 px-2">Qty</th>
                      <th className="text-right py-1 px-2">Total</th>
                      <th className="text-right py-1 px-2">Saving</th>
                      <th className="py-1 px-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {draft.items.map((item) => {
                      const price = item.unitPrice ?? 0;
                      const saving = item.avg30d && item.avg30d > price
                        ? (item.avg30d - price) * item.quantity
                        : 0;
                      const supplier = item.vhubName
                        || (item.source === 'producer' ? 'Producer' : item.source === 'hub' ? 'Hub' : '—');
                      return (
                        <tr key={item._id} className="border-b hover:bg-gray-50">
                          <td className="py-1 px-2 capitalize">{supplier}</td>
                          <td className="py-1 px-2 text-xs text-gray-500">{item.category || '—'}</td>
                          <td className="py-1 px-2">{item.productName}</td>
                          <td className="py-1 px-2 text-right">${price.toFixed(2)}</td>
                          <td className="py-1 px-2 text-center">
                            {!isReadOnly ? (
                              <div className="inline-flex items-center gap-1">
                                <button
                                  onClick={() => handleUpdateQuantity(item.productId, Math.max(0, item.quantity - 1))}
                                  className="w-5 h-5 border rounded text-xs"
                                >
                                  −
                                </button>
                                <span className="w-6 text-center">{item.quantity}</span>
                                <button
                                  onClick={() => handleUpdateQuantity(item.productId, item.quantity + 1)}
                                  className="w-5 h-5 border rounded text-xs"
                                >
                                  +
                                </button>
                              </div>
                            ) : (
                              <span>{item.quantity}</span>
                            )}
                          </td>
                          <td className="py-1 px-2 text-right">
                            ${(price * item.quantity).toFixed(2)}
                          </td>
                          <td className="py-1 px-2 text-right text-green-700">
                            {saving > 0 ? `$${saving.toFixed(2)}` : '—'}
                          </td>
                          <td className="py-1 px-2 text-right">
                            {!isReadOnly && (
                              <button
                                onClick={() => handleRemoveItem(item._id)}
                                className="text-red-600 text-xs"
                              >
                                ✕
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="font-semibold border-t">
                      <td colSpan={5} className="py-1 px-2 text-right">Total</td>
                      <td className="py-1 px-2 text-right">${draft.total?.toFixed(2)}</td>
                      <td className="py-1 px-2 text-right text-green-700">
                        ${draft.items.reduce((s, it) => {
                          const sv = it.avg30d && it.avg30d > it.unitPrice ? (it.avg30d - it.unitPrice) * it.quantity : 0;
                          return s + sv;
                        }, 0).toFixed(2)}
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}

          {/* Suppliers list */}
          <div className="mb-4">
            <h2 className="text-lg font-bold mb-3">🏪 Suppliers</h2>

            {suppliersLoading ? (
              <div className="text-center py-8 text-gray-500">Loading suppliers...</div>
            ) : suppliers.length === 0 ? (
              <div className="text-center py-8 bg-gray-50 rounded-lg text-gray-500">
                No suppliers available. Contact your coordinator.
              </div>
            ) : (
              <div className="space-y-2">
                {suppliers.map(supplier => {
                  const isExpanded = expandedSuppliers[supplier.id];
                  const products = supplierProducts[supplier.id] || [];
                  const pagination = supplierPages[supplier.id];
                  const categories = supplierCategories[supplier.id] || [];
                  const currentFilter = supplierCategoryFilter[supplier.id];

                  return (
                    <div key={supplier.id} className="border rounded-lg overflow-hidden bg-white">
                      <div
                        className={`flex justify-between items-center p-3 cursor-pointer transition-colors ${
                          isExpanded ? 'bg-green-50' : 'bg-gray-50 hover:bg-gray-100'
                        }`}
                        onClick={() => toggleSupplier(supplier.id)}
                      >
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <span className="font-semibold truncate">{supplier.name}</span>
                          <span className="text-xs bg-gray-200 px-2 py-0.5 rounded-full">
                            {supplier.type === 'vhub' ? '🛒' : supplier.type === 'hub' ? '🏪' : '🚜'} {supplier.type}
                          </span>
                          <span className="text-sm text-gray-500">({supplier.productCount})</span>
                        </div>
                        <span className="text-gray-400 ml-2">{isExpanded ? '▼' : '▶'}</span>
                      </div>

                      {isExpanded && (
                        <div className="p-3">
                          {/* Search within this supplier */}
                          <input
                            type="search"
                            value={supplierSearch[supplier.id] || ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              setSupplierSearch((prev) => ({ ...prev, [supplier.id]: value }));
                              fetchSupplierProducts(supplier.id, 1, currentFilter, value);
                            }}
                            placeholder={`Search ${supplier.name}...`}
                            className="w-full px-3 py-2 border rounded-lg text-sm mb-2 focus:ring-2 focus:ring-green-500"
                          />
                          {/* Category chips */}
                          {categories.length > 1 && (
                            <div className="flex flex-wrap gap-1 mb-3">
                              <button
                                onClick={() => setCategoryFilter(supplier.id, null)}
                                className={`px-2 py-1 text-xs rounded ${
                                  !currentFilter
                                    ? 'bg-green-600 text-white'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                }`}
                              >
                                All
                              </button>
                              {categories.map(cat => (
                                <button
                                  key={cat.id}
                                  onClick={() => setCategoryFilter(supplier.id, cat.id)}
                                  className={`px-2 py-1 text-xs rounded ${
                                    currentFilter === cat.id
                                      ? 'bg-green-600 text-white'
                                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                  }`}
                                >
                                  {cat.id} ({cat.count})
                                </button>
                              ))}
                            </div>
                          )}

                          {/* Product grid */}
                          {products.length === 0 ? (
                            <div className="text-center py-6 text-gray-400 text-sm">
                              Loading products...
                            </div>
                          ) : (
                            <>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {products.map(product => (
                                  <MemberDraftProductCard
                                    key={product._id}
                                    product={product}
                                    isInDraft={isProductInDraft(product._id)}
                                    onAdd={handleAddItem}
                                    onRemove={() => {
                                      const item = draft?.items?.find(
                                        i => i.productId?.toString() === product._id?.toString()
                                      );
                                      if (item) handleRemoveItem(item._id);
                                    }}
                                    onUpdateQuantity={handleUpdateQuantity}
                                    quantity={getItemQuantity(product._id)}
                                    submitting={submitting}
                                    readOnly={isReadOnly}
                                  />
                                ))}
                              </div>

                              {/* Load more */}
                              {pagination && pagination.page < pagination.pages && (
                                <div className="mt-3 text-center">
                                  <button
                                    onClick={() => loadMore(supplier.id)}
                                    disabled={loadingMore[supplier.id]}
                                    className="text-sm bg-gray-100 text-gray-700 px-4 py-2 rounded hover:bg-gray-200 disabled:opacity-50"
                                  >
                                    {loadingMore[supplier.id]
                                      ? 'Loading...'
                                      : `Load more (${pagination.page * pagination.limit} of ${pagination.total})`}
                                  </button>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Submit buttons */}
          {canSubmit && hasActiveDraft && (
            <div className="mt-6">
              <button
                onClick={submitDraft}
                disabled={submitting}
                className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 font-semibold"
              >
                {submitting ? 'Submitting...' : '📤 Submit Draft to Coordinator'}
              </button>
              <p className="text-center text-sm text-gray-400 mt-2">
                Once submitted, you cannot make changes until the coordinator consolidates.
              </p>
            </div>
          )}

          {isConsolidated && hasActiveDraft && (
            <div className="mt-6">
              <button
                onClick={submitRevisions}
                disabled={submitting}
                className="w-full bg-purple-600 text-white py-3 rounded-lg hover:bg-purple-700 disabled:bg-gray-400 font-semibold"
              >
                {submitting ? 'Submitting...' : '📤 Submit Revisions to Coordinator'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
